import React, {useContext, useState, useMemo, useEffect} from 'react';
import classnames from 'classnames';

import {Checkbox} from '@homeaway/react-form-components';
import {ButtonLink} from '@homeaway/react-buttons';
import {
    TrackingContext,
    TrackingContextValue,
} from '@homeaway/react-event-tracking';
import Tooltip from '@homeaway/react-tooltip';
import {SVGIcon} from '@homeaway/react-svg';
import {INFO__16} from '@homeaway/svg-defs';

import {Refinement} from '../FilterGroup/FilterGroup';
import {Props as FilterBarProps} from '../FilterBarV2/FilterBarV2';

import {triggerEdapEventForCheckbox} from '../../utils/edap';

import './FilterGroupFilters.less';

const MIN_COUNT = 2;

function getVisibleFilters(
    viewAll: boolean,
    filters: Refinement[],
    selectedFilters: string[],
    showMax: number
): Refinement[] {
    if (viewAll) {
        return filters;
    }

    let i = 1;
    const visibleFilters: Refinement[] = [];

    filters.forEach((filterOption, index) => {
        const isChecked = selectedFilters.includes(
            filterOption.filter.refineByQueryArgument
        );

        if (isChecked) {
            visibleFilters.push(filterOption);
            /* istanbul ignore else */
            if (index <= showMax) {
                i += 1;
            }
        } else if (i <= showMax) {
            i += 1;
            visibleFilters.push(filterOption);
        }
    });

    return visibleFilters;
}

type ExtendedProp = Pick<
    FilterBarProps,
    'selectedFilters' | 'isMobile' | 'onFilterToggle'
>;

export interface Props extends ExtendedProp {
    filters: Refinement[];
    groupId?: string;
    showMax?: number;
    resetViewAll?: boolean;
    twoColumns?: boolean;
}

export default function FilterGroupFilters({
    filters,
    groupId,
    selectedFilters,
    onFilterToggle,
    showMax = 6,
    resetViewAll = false,
    twoColumns = false,
    isMobile = false,
}: Props) {
    const [viewAll, setViewAll] = useState(false);
    const edapContext: TrackingContextValue = useContext(TrackingContext);

    function onChange(event) {
        triggerEdapEventForCheckbox(event, edapContext);
        onFilterToggle(event.currentTarget);
    }

    function onViewAllClick() {
        setViewAll(true);
    }

    const visibleOptions = useMemo(
        () => getVisibleFilters(viewAll, filters, selectedFilters, showMax),
        [viewAll, filters, selectedFilters, showMax]
    );

    useEffect(() => {
        setViewAll(false);
    }, [resetViewAll]);

    const className = classnames('FilterGroupFilters__List', {
        'FilterGroupFilters__List--columns': twoColumns && !isMobile,
    });

    return (
        <div className="FilterGroupFilters">
            <ul className={className}>
                {/* eslint-disable-next-line complexity */}
                {visibleOptions.map(({filter, count}) => {
                    const isChecked = selectedFilters.includes(
                        filter.refineByQueryArgument
                    );
                    const isDisabled =
                        !isChecked && typeof count !== 'undefined'
                            ? count <= MIN_COUNT
                            : false;

                    return (
                        <li
                            key={filter.refineByQueryArgument}
                            className={'FilterGroupFilters__ListItem'}
                        >
                            <div
                                className={'FilterGroupFilters__ListItemInner'}
                            >
                                <Checkbox
                                    name={filter.refineByQueryArgument}
                                    data-groupid={groupId}
                                    label={filter.name}
                                    checked={isChecked}
                                    disabled={isDisabled}
                                    onChange={onChange}
                                />
                                {filter.description && (
                                    <Tooltip
                                        content={filter.description}
                                        /* istanbul ignore next */
                                        delayShow={
                                            isMobile
                                                ? /* istanbul ignore next */ 0
                                                : 400
                                        }
                                        fadeOut={false}
                                        parentNode={
                                            /* istanbul ignore next */
                                            typeof document !== 'undefined'
                                                ? document.body
                                                : null
                                        }
                                        placement="bottom"
                                        trigger={
                                            /* istanbul ignore next */
                                            isMobile ? 'click' : 'hover focus'
                                        }
                                        wrapperClassName="FilterGroupFilters__Tooltip"
                                    >
                                        <SVGIcon
                                            markup={INFO__16}
                                            aria-hidden
                                        />
                                    </Tooltip>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
            {visibleOptions.length < filters.length && (
                <ButtonLink
                    className="FilterGroupFilters__ViewAll"
                    label="View all"
                    size="xs"
                    onClick={onViewAllClick}
                />
            )}
        </div>
    );
}
